// src/pages/EventQrCodePage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { QRCodeSVG } from 'qrcode.react';

export default function EventQrCodePage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadEventAndQr = async () => {
      try {
        // 이벤트 정보 로드
        const eventResponse = await axios.get(`/api/events/detail?eventId=${eventId}`);
        setEvent(eventResponse.data);

        // QR 코드 데이터 로드
        const qrResponse = await axios.get(`/api/admin/events/${eventId}/qr-code`);
        setQrData(qrResponse.data);
        
        setLoading(false);
      } catch (error) {
        setError('Failed to load event information');
        setLoading(false);
      }
    };

    loadEventAndQr();
  }, [eventId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-800 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading QR code...</p>
        </div>
      </div>
    );
  }

  if (error || !event || !qrData) {
    return (
      <div className="min-h-screen bg-gray-800 text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Error</h1>
          <p className="text-gray-400 mb-6">{error || 'Event or QR data not found'}</p>
          <button 
            onClick={() => navigate('/events')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-800 text-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2">Event Check-in QR Code</h1>
          <h2 className="text-2xl text-blue-400 mb-4">{event.title}</h2>
          <p className="text-gray-400">
            {new Date(event.eventDateTime).toLocaleString()}
          </p>
        </div>

        {/* QR Code Display */}
        <div className="bg-white p-8 rounded-lg shadow-2xl text-center">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">Scan this QR code to check in</h3>
          
          {/* Large QR Code */}
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-white border-4 border-gray-200 rounded-lg">
              <QRCodeSVG 
                value={JSON.stringify(qrData.qrData)} 
                size={300}
                level="M"
              />
            </div>
          </div>

          {/* Event Info */}
          <div className="bg-gray-100 p-6 rounded-lg text-gray-800">
            <h4 className="text-lg font-semibold mb-3">Event Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Event:</strong> {event.title}</p>
                <p><strong>Date:</strong> {new Date(event.eventDateTime).toLocaleDateString()}</p>
                <p><strong>Time:</strong> {new Date(event.eventDateTime).toLocaleTimeString()}</p>
              </div>
              <div>
                <p><strong>Location:</strong> {event.location}</p>
                <p><strong>Branch:</strong> {event.branch}</p>
                <p><strong>Event ID:</strong> {eventId}</p>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="text-lg font-semibold text-blue-800 mb-2">Instructions for Staff</h4>
            <ul className="text-sm text-blue-700 text-left space-y-1">
              <li>• Display this QR code at the event entrance</li>
              <li>• Participants should scan this code using their phones</li>
              <li>• Make sure the QR code is clearly visible and well-lit</li>
              <li>• Check-in is available 30 minutes before the event starts</li>
            </ul>
          </div>
        </div>

        {/* Print Button */}
        <div className="text-center mt-8">
          <button
            onClick={() => window.print()}
            className="bg-green-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-green-700 transition-colors"
          >
            🖨️ Print QR Code
          </button>
        </div>

        {/* Back Button */}
        <div className="text-center mt-4">
          <button
            onClick={() => navigate(`/events/${eventId}`)}
            className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            ← Back to Event
          </button>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-area, .print-area * {
            visibility: visible;
          }
          .print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
